/**
 * The Seller Trust Index report is the only report whose API speaks its own
 * platform vocabulary ('Roku', 'Amazon', 'Samsung', 'tvOS', 'LG') instead of
 * the lowercase device slug ('roku', 'firetv', ...) every other report uses.
 *
 * The slug stays canonical everywhere in the UI -- routes, filter values,
 * component inputs -- and is translated to the API vocabulary only at the
 * request boundary. MR-2633
 */
export const STI_PLATFORM_BY_DEVICE: { [device: string]: string } = {
  roku: 'Roku',
  firetv: 'Amazon',
  samsung: 'Samsung',
  tvos: 'tvOS',
  lgtv: 'LG'
};

const DEVICE_BY_STI_PLATFORM: { [platform: string]: string } = Object.keys(
  STI_PLATFORM_BY_DEVICE
).reduce((map, device) => {
  map[STI_PLATFORM_BY_DEVICE[device].toLowerCase()] = device;
  return map;
}, {});

/**
 * Normalizes either form -- a device slug or a legacy Seller Trust Index
 * platform value -- to the canonical device slug. Seller Trust Index URLs used
 * to carry the API value as their slug (/seller-trust-index/Amazon/...), and
 * those links are public and indexed, so they still have to resolve.
 *
 * Returns '' for anything that is not a known large screen device.
 */
export function toDeviceSlug(value: string): string {
  if (!value || typeof value !== 'string') return '';
  const lowered = value.toLowerCase();
  if (STI_PLATFORM_BY_DEVICE[lowered]) return lowered;
  return DEVICE_BY_STI_PLATFORM[lowered] || '';
}

/**
 * Translates a device slug to the platform value the Seller Trust Index API
 * expects. Use this when building a request, never to pick a label or a route.
 */
export function toStiPlatformName(value: string): string {
  const device = toDeviceSlug(value);
  return device ? STI_PLATFORM_BY_DEVICE[device] : '';
}
